/**
 * Number lines: exact tick values, labels, jumps and marked points.
 *
 * Values are kept as reduced fractions of whole numbers, so 0.1 + 0.2 lands exactly on
 * 0.3 and a line of fifths has a tick at exactly 3/5. Floating point is used only for
 * the x position of a tick on the page.
 */

import { gcd } from "@/lib/tool-kit/worksheets/fractions";
import { type Rng, shuffled } from "@/lib/tool-kit/worksheets/random";

export interface Rational {
  n: number;
  d: number;
}

export type LineKind = "integer" | "decimal" | "fraction";
export type FractionForm = "mixed" | "improper" | "unreduced";
export type LabelMode = "all" | "ends" | "some" | "none";
export type LineError =
  | "range"
  | "step"
  | "integer"
  | "denominator"
  | "tooMany";

export const MAX_TICKS = 150;
export const MIN_DENOMINATOR = 2;
export const MAX_DENOMINATOR = 20;
const MAX_DECIMALS = 4;

/* -------------------------------------------------------------------- rationals */

export function rational(n: number, d = 1): Rational {
  const sign = d < 0 ? -1 : 1;
  const divisor = gcd(n, d) || 1;
  return { n: (sign * n) / divisor, d: (sign * d) / divisor };
}

export const addR = (a: Rational, b: Rational) =>
  rational(a.n * b.d + b.n * a.d, a.d * b.d);
export const subR = (a: Rational, b: Rational) =>
  rational(a.n * b.d - b.n * a.d, a.d * b.d);
export const mulIntR = (a: Rational, k: number) => rational(a.n * k, a.d);
export const toNumber = (a: Rational) => a.n / a.d;
export const compareR = (a: Rational, b: Rational) => a.n * b.d - b.n * a.d;

const WHOLE = /^-?\d+$/;
const DECIMAL = /^-?(?:\d+[.,]\d*|[.,]\d+)$/;
const FRACTION = /^(-?\d+)\s*\/\s*(\d+)$/;
const MIXED = /^(-?)(\d+)\s+(\d+)\s*\/\s*(\d+)$/;

/** A typed value: `3`, `-2.5`, `0,75`, `3/4` or `1 3/4`. Null when unreadable. */
export function parseValue(raw: string): Rational | null {
  const text = raw.trim().replace("−", "-");
  if (WHOLE.test(text)) {
    return rational(Number(text));
  }
  const mixed = MIXED.exec(text);
  if (mixed) {
    const denominator = Number(mixed[4]);
    if (denominator === 0) {
      return null;
    }
    const magnitude = Number(mixed[2]) * denominator + Number(mixed[3]);
    return rational(mixed[1] === "-" ? -magnitude : magnitude, denominator);
  }
  const fraction = FRACTION.exec(text);
  if (fraction) {
    return Number(fraction[2]) === 0
      ? null
      : rational(Number(fraction[1]), Number(fraction[2]));
  }
  if (DECIMAL.test(text)) {
    const normalised = text.replace(",", ".");
    const places = (normalised.split(".")[1] ?? "").length;
    if (places > MAX_DECIMALS) {
      return null;
    }
    const scale = 10 ** places;
    return rational(Math.round(Number(normalised) * scale), scale);
  }
  return null;
}

/** Decimal places needed to write every one of the values exactly (0 to 4), else null. */
export function decimalPlaces(values: readonly Rational[]): number | null {
  for (let places = 0; places <= MAX_DECIMALS; places += 1) {
    const scale = 10 ** places;
    if (values.every((v) => (v.n * scale) % v.d === 0)) {
      return places;
    }
  }
  return null;
}

/** `-0.30`: a rational written with exactly `places` decimals. */
export function formatDecimal(value: Rational, places: number): string {
  const scale = 10 ** places;
  const scaled = Math.round((value.n * scale) / value.d);
  const magnitude = Math.abs(scaled);
  const whole = Math.trunc(magnitude / scale);
  const sign = scaled < 0 ? "−" : "";
  if (places === 0) {
    return `${sign}${whole}`;
  }
  const fractionPart = String(magnitude % scale).padStart(places, "0");
  return `${sign}${whole}.${fractionPart}`;
}

/* ----------------------------------------------------------------------- labels */

export interface FractionLabel {
  /** `−` for a negative value. */
  sign: string;
  whole: string | null;
  n: string;
  d: string;
}

export type TickLabel =
  | { kind: "plain"; text: string }
  | { kind: "fraction"; label: FractionLabel };

/**
 * A fraction tick's label. `mixed` and `improper` reduce (1/2, 1 1/4 or 5/4);
 * `unreduced` keeps the line's own denominator (2/4, 5/4, and 0/4 at zero).
 */
export function fractionLabel(
  value: Rational,
  denominator: number,
  form: FractionForm
): TickLabel {
  const negative = value.n < 0;
  const sign = negative ? "−" : "";
  const magnitude = Math.abs(value.n);
  if (form === "unreduced") {
    const top = (magnitude * denominator) / value.d;
    return {
      kind: "fraction",
      label: { sign, whole: null, n: String(top), d: String(denominator) },
    };
  }
  const whole = Math.trunc(magnitude / value.d);
  const rest = magnitude % value.d;
  if (rest === 0) {
    return { kind: "plain", text: `${sign}${whole}` };
  }
  if (form === "improper") {
    return {
      kind: "fraction",
      label: { sign, whole: null, n: String(magnitude), d: String(value.d) },
    };
  }
  return {
    kind: "fraction",
    label: {
      sign,
      whole: whole > 0 ? String(whole) : null,
      n: String(rest),
      d: String(value.d),
    },
  };
}

/* ------------------------------------------------------------------------- line */

export interface LineSpec {
  kind: LineKind;
  start: Rational;
  end: Rational;
  step: Rational;
  /** Fraction lines only. */
  denominator: number;
  /** Label every k-th tick (1 labels every tick). */
  labelEvery: number;
  fractionForm: FractionForm;
}

export interface LineInputs {
  kind: LineKind;
  start: string;
  end: string;
  step: string;
  denominator: number;
  labelEvery: number;
  fractionForm: FractionForm;
}

export interface Tick {
  index: number;
  value: Rational;
  /** Position along the line, 0 at the start and 1 at the end. */
  fraction: number;
  /** A label (or a box for a hidden label) goes under this tick. */
  labeled: boolean;
  label: TickLabel;
}

export interface BuiltLine {
  spec: LineSpec;
  ticks: Tick[];
  /** Decimal places used for labels and jump sizes (0 unless the line is decimal). */
  places: number;
}

export type LineResult =
  | { ok: true; line: BuiltLine }
  | { ok: false; error: LineError };

function isWhole(value: Rational): boolean {
  return value.d === 1;
}

/** Reads the typed settings into a spec, or says which one is wrong. */
export function specFromInputs(
  inputs: LineInputs
): { ok: true; spec: LineSpec } | { ok: false; error: LineError } {
  const start = parseValue(inputs.start);
  const end = parseValue(inputs.end);
  if (!(start && end) || compareR(start, end) >= 0) {
    return { ok: false, error: "range" };
  }
  const labelEvery = Math.max(1, Math.round(inputs.labelEvery) || 1);
  if (inputs.kind === "fraction") {
    const d = Math.round(inputs.denominator);
    if (d < MIN_DENOMINATOR || d > MAX_DENOMINATOR) {
      return { ok: false, error: "denominator" };
    }
    if (!(isWhole(start) && isWhole(end))) {
      return { ok: false, error: "integer" };
    }
    return {
      ok: true,
      spec: {
        kind: "fraction",
        start,
        end,
        step: rational(1, d),
        denominator: d,
        labelEvery,
        fractionForm: inputs.fractionForm,
      },
    };
  }
  const step = parseValue(inputs.step);
  if (!step || step.n <= 0) {
    return { ok: false, error: "step" };
  }
  if (
    inputs.kind === "integer" &&
    !(isWhole(start) && isWhole(end) && isWhole(step))
  ) {
    return { ok: false, error: "integer" };
  }
  return {
    ok: true,
    spec: {
      kind: inputs.kind,
      start,
      end,
      step,
      denominator: 1,
      labelEvery,
      fractionForm: inputs.fractionForm,
    },
  };
}

/** Builds the ticks, or says why the line cannot be drawn. */
export function buildLine(spec: LineSpec): LineResult {
  const span = subR(spec.end, spec.start);
  const steps = rational(span.n * spec.step.d, span.d * spec.step.n);
  if (!isWhole(steps) || steps.n < 1) {
    return { ok: false, error: "step" };
  }
  if (steps.n + 1 > MAX_TICKS) {
    return { ok: false, error: "tooMany" };
  }
  const places =
    spec.kind === "decimal"
      ? decimalPlaces([spec.start, spec.end, spec.step])
      : 0;
  if (places === null) {
    return { ok: false, error: "step" };
  }
  const total = steps.n;
  const spanNumber = toNumber(span);
  const ticks: Tick[] = Array.from({ length: total + 1 }, (_, index) => {
    const value = addR(spec.start, mulIntR(spec.step, index));
    const label: TickLabel =
      spec.kind === "fraction"
        ? fractionLabel(value, spec.denominator, spec.fractionForm)
        : { kind: "plain", text: formatDecimal(value, places) };
    return {
      index,
      value,
      fraction: toNumber(subR(value, spec.start)) / spanNumber,
      labeled: index % spec.labelEvery === 0 || index === total,
      label,
    };
  });
  return { ok: true, line: { spec, ticks, places } };
}

/**
 * Tick indexes whose label is replaced by an empty box. `ends` and `some` keep the first
 * and last labels as a reference; `some` hides `fraction` of the rest at random; `none`
 * hides every label.
 */
export function hiddenLabels(
  line: BuiltLine,
  mode: LabelMode,
  fraction: number,
  rng: Rng
): Set<number> {
  const labeled = line.ticks.filter((t) => t.labeled).map((t) => t.index);
  const last = line.ticks.length - 1;
  if (mode === "all") {
    return new Set();
  }
  if (mode === "none") {
    return new Set(labeled);
  }
  const inner = labeled.filter((i) => i !== 0 && i !== last);
  if (mode === "ends") {
    return new Set(inner);
  }
  const wanted = Math.round(Math.min(1, Math.max(0, fraction)) * inner.length);
  return new Set(shuffled(rng, inner).slice(0, wanted));
}

/* -------------------------------------------------------------- jumps and points */

export interface Jump {
  from: Rational;
  to: Rational;
  /** The jump's size with a sign: `+3`, `−1.5`, `+1/2`. */
  label: string;
}

export interface Parsed<T> {
  items: T[];
  /** Lines that could not be read or fall outside the line, as typed. */
  rejected: string[];
}

const JUMP_SEPARATOR = /\s*(?:->|→|>|\bto\b|,)\s*/;
const ITEM_SEPARATOR = /[;\n]+/;
const SEMICOLON_OR_NEWLINE = /[;\n]/;

function inside(spec: LineSpec, value: Rational): boolean {
  return compareR(value, spec.start) >= 0 && compareR(value, spec.end) <= 0;
}

function sizeLabel(
  difference: Rational,
  spec: LineSpec,
  places: number
): string {
  const sign = difference.n < 0 ? "−" : "+";
  const magnitude = rational(Math.abs(difference.n), difference.d);
  if (spec.kind === "fraction") {
    const label = fractionLabel(magnitude, spec.denominator, "mixed");
    if (label.kind === "plain") {
      return `${sign}${label.text}`;
    }
    const { whole, n, d } = label.label;
    return `${sign}${whole ? `${whole} ` : ""}${n}/${d}`;
  }
  return `${sign}${formatDecimal(magnitude, places)}`;
}

/** One jump per line (or per `;`): `0 to 3`, `3 -> 7`, `1/4, 3/4`. */
export function parseJumps(text: string, line: BuiltLine): Parsed<Jump> {
  const items: Jump[] = [];
  const rejected: string[] = [];
  for (const raw of text.split(ITEM_SEPARATOR)) {
    const entry = raw.trim();
    if (entry === "") {
      continue;
    }
    const parts = entry.split(JUMP_SEPARATOR);
    const from = parts.length === 2 ? parseValue(parts[0] ?? "") : null;
    const to = parts.length === 2 ? parseValue(parts[1] ?? "") : null;
    const fits =
      from &&
      to &&
      inside(line.spec, from) &&
      inside(line.spec, to) &&
      compareR(from, to) !== 0;
    if (from && to && fits) {
      const label = sizeLabel(
        subR(to, from),
        line.spec,
        Math.max(line.places, decimalPlaces([from, to]) ?? 0)
      );
      items.push({ from, to, label });
    } else {
      rejected.push(entry);
    }
  }
  return { items, rejected };
}

/** Marked points, separated by `;` or new lines (commas also work for whole numbers). */
export function parsePoints(text: string, line: BuiltLine): Parsed<Rational> {
  const items: Rational[] = [];
  const rejected: string[] = [];
  const hasSemicolons = SEMICOLON_OR_NEWLINE.test(text);
  const pieces = hasSemicolons ? text.split(ITEM_SEPARATOR) : text.split(",");
  for (const raw of pieces) {
    const entry = raw.trim();
    if (entry === "") {
      continue;
    }
    const value = parseValue(entry);
    if (value && inside(line.spec, value)) {
      items.push(value);
    } else {
      rejected.push(entry);
    }
  }
  return { items, rejected };
}

/** Where a value falls along the line, 0 to 1. */
export function positionOf(value: Rational, spec: LineSpec): number {
  return (
    toNumber(subR(value, spec.start)) / toNumber(subR(spec.end, spec.start))
  );
}

/** A point's name: A to Z, then A2 to Z2 and so on. */
export function pointLetter(index: number): string {
  const letter = String.fromCharCode(65 + (index % 26));
  const cycle = Math.floor(index / 26);
  return cycle === 0 ? letter : `${letter}${cycle + 1}`;
}
